import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Star, Eye, EyeOff, ArrowUp, ArrowDown, PlusCircle, Play, FileText } from "lucide-react";
import { api, mediaUrl, thumbUrl, formatApiError } from "@/lib/api";

export default function AdminWork() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/items", { params: { status, category, q } }).then((r) => r.data),
      api.get("/admin/categories").then((r) => r.data),
    ])
      .then(([its, cats]) => {
        setItems(its);
        setCategories(cats);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, category, q]);

  const patch = async (item, changes) => {
    try {
      await api.put(`/admin/items/${item.id}`, { ...item, ...changes });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete “${item.title}”? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/items/${item.id}`);
      toast.success("Work deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const move = async (index, dir) => {
    const ids = items.map((i) => i.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    try {
      await api.put("/admin/items/reorder", { ids });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const statusPill = (s) =>
    s === "published"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : s === "hidden"
      ? "bg-charcoal-900/5 text-charcoal-500 border-charcoal-900/15"
      : "bg-amber-50 text-amber-700 border-amber-200";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl text-charcoal-900">All Work</h1>
          <p className="text-sm text-charcoal-500 mt-1">{items.length} items</p>
        </div>
        <Link
          to="/admin/work/new"
          data-testid="admin-add-work-button"
          className="inline-flex items-center gap-2 rounded-full bg-charcoal-900 text-parchment text-sm font-medium px-5 py-2.5 hover:bg-copper-600 transition-colors"
        >
          <PlusCircle className="w-4 h-4" /> Add Work
        </Link>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <input
          data-testid="admin-work-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search work..."
          className="bg-surface-white border border-charcoal-900/15 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-copper-600 w-56"
        />
        <select
          data-testid="admin-work-status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="bg-surface-white border border-charcoal-900/15 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-copper-600"
        >
          <option value="">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="hidden">Hidden</option>
        </select>
        <select
          data-testid="admin-work-category-filter"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="bg-surface-white border border-charcoal-900/15 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-copper-600"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-charcoal-500 font-mono">Loading...</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-charcoal-900/20 bg-surface-white/60 p-10 text-center" data-testid="admin-work-empty">
          <p className="font-serif text-xl">No work yet</p>
          <p className="text-sm text-charcoal-500 mt-1">Add your first piece or use Bulk Upload for a whole folder.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="admin-work-list">
          {items.map((item, i) => {
            const cover = item.media?.find((m) => m.media_type === "image") || item.media?.[0];
            return (
              <div
                key={item.id}
                data-testid={`admin-work-row-${item.slug}`}
                className="bg-surface-white border border-charcoal-900/10 rounded-xl p-4 flex items-center gap-4"
              >
                <div className="flex flex-col gap-1">
                  <button data-testid={`work-move-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="text-charcoal-400 hover:text-copper-600 disabled:opacity-30">
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`work-move-down-${i}`} onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down" className="text-charcoal-400 hover:text-copper-600 disabled:opacity-30">
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-surface-muted shrink-0 flex items-center justify-center">
                  {cover?.media_type === "image" ? (
                    <img src={thumbUrl(cover)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : cover?.media_type === "video" ? (
                    <Play className="w-5 h-5 text-copper-600" />
                  ) : cover?.media_type === "pdf" ? (
                    <FileText className="w-5 h-5 text-copper-600" />
                  ) : (
                    <span className="font-mono text-[9px] text-charcoal-400">NO MEDIA</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-charcoal-900 truncate">{item.title}</p>
                  <p className="text-xs text-charcoal-500 mt-0.5 truncate">
                    {catMap[item.category_id]?.name || "Uncategorized"}
                    {item.year ? ` · ${item.year}` : ""}
                    {item.media?.length ? ` · ${item.media.length} file${item.media.length > 1 ? "s" : ""}` : ""}
                  </p>
                </div>
                <span className={`hidden sm:inline-block font-mono text-[10px] uppercase tracking-wide border rounded-full px-2.5 py-1 ${statusPill(item.status)}`}>
                  {item.status}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`work-featured-${i}`}
                    onClick={() => patch(item, { featured: !item.featured })}
                    aria-label="Toggle featured"
                    aria-pressed={item.featured}
                    className={`p-2 rounded-lg transition-colors ${item.featured ? "text-copper-600" : "text-charcoal-400 hover:text-copper-600"}`}
                  >
                    <Star className="w-4 h-4" fill={item.featured ? "currentColor" : "none"} />
                  </button>
                  <button
                    data-testid={`work-publish-${i}`}
                    onClick={() => patch(item, { status: item.status === "published" ? "hidden" : "published" })}
                    aria-label={item.status === "published" ? "Unpublish" : "Publish"}
                    className="p-2 rounded-lg text-charcoal-400 hover:text-copper-600 transition-colors"
                  >
                    {item.status === "published" ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <Link
                    to={`/admin/work/${item.id}/edit`}
                    data-testid={`work-edit-${i}`}
                    aria-label={`Edit ${item.title}`}
                    className="p-2 rounded-lg text-charcoal-400 hover:text-copper-600 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </Link>
                  <button
                    data-testid={`work-delete-${i}`}
                    onClick={() => remove(item)}
                    aria-label={`Delete ${item.title}`}
                    className="p-2 rounded-lg text-charcoal-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
